import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, UserRole } from '../types';
import { useAuth } from './AuthContext';
import { 
  SESSION_KEYS, 
  saveUserData, 
  loadUserData, 
  clearSessionStorage 
} from '../utils/sessionStorage';

interface UserContextType {
  currentUser: User | null;
  isAdmin: boolean;
  isOwner: boolean;
  isBranchManager: boolean;
  isEmployee: boolean;
  loading: boolean;
  error: string | null;
  refreshUser: () => Promise<void>;
  updateUserData: (userData: Partial<User>) => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const { user: authUser, loading: authLoading, error: authError, refreshUser: authRefreshUser } = useAuth();
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Role-based access control helpers
  const isAdmin = currentUser?.role === UserRole.ADMIN;
  const isOwner = currentUser?.role === UserRole.OWNER;
  const isBranchManager = currentUser?.role === UserRole.BRANCH_MANAGER;
  const isEmployee = currentUser?.role === UserRole.EMPLOYEE;

  // Initialize user data from session storage or auth context
  const initializeUserData = () => {
    const savedUserData = loadUserData<User>();
    
    if (savedUserData && authUser) {
      // Merge saved data with auth user data (auth user takes precedence for critical fields)
      const mergedUser = {
        ...savedUserData,
        id: authUser.id,
        email: authUser.email,
        role: authUser.role,
        status: authUser.status,
        companyId: authUser.companyId,
      };
      setCurrentUser(mergedUser);
      console.log('Restored user data from session storage:', mergedUser);
    } else if (authUser) {
      setCurrentUser(authUser);
      saveUserData(authUser);
      console.log('Saved user data to session storage:', authUser);
    } else {
      setCurrentUser(null);
    }
  };

  // Enhanced refresh user function
  const refreshUser = async () => {
    try {
      setLoading(true);
      await authRefreshUser();
      // The auth refresh will trigger the useEffect below to update currentUser
    } catch (err) {
      setError('Failed to refresh user data');
      console.error('Error refreshing user:', err);
    } finally {
      setLoading(false);
    }
  };

  // Update user data function
  const updateUserData = (userData: Partial<User>) => {
    if (currentUser) {
      const updatedUser = { ...currentUser, ...userData };
      setCurrentUser(updatedUser);
      saveUserData(updatedUser);
      console.log('Updated user data:', updatedUser);
    }
  };

  // Initialize user data when auth user changes
  useEffect(() => {
    if (authUser) {
      initializeUserData();
    } else {
      setCurrentUser(null);
      clearSessionStorage([SESSION_KEYS.USER_DATA]);
    }
    setLoading(authLoading);
    setError(authError);
  }, [authUser, authLoading, authError]);

  const value: UserContextType = {
    currentUser,
    isAdmin,
    isOwner,
    isBranchManager,
    isEmployee,
    loading,
    error,
    refreshUser,
    updateUserData,
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
